import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { reaction } from "mobx";
import './SequenceVideoClip.styles.scss';
import {SequenceStateStore} from "../store/SequenceStateStore";
import {useAssetStore} from "../../storage/AssetStore";
import {SequenceCompositionService} from "../services/SequenceCompositionService";
import SequenceClip from "./SequenceClip";
import WaveformDisplay from "../../librettoEditor/modules/timeline/components/WaveformDisplay/WaveformDisplay";
import {ProjectAssetsStore} from "../../state/ProjectAssetsStore";

interface VideoClipProps {
    clipId: string;
    layerId: string;
}

export const SequenceVideoClip: React.FC<VideoClipProps> = observer(({ clipId, layerId }) => {
    const [filename, setFilename] = useState<string | undefined>(SequenceStateStore.getClipTitle(clipId));
    const [thumbnail, setThumbnail] = useState<string | undefined>(undefined);
    const [peaks, setPeaks] = useState<Float32Array | null>(null);
    const [studioSoundEnabled, setStudioSoundEnabled] = useState(false);
    const { GetWaveformData, GetThumbnailUrl } = useAssetStore();

    // Listen for Studio Sound changes from the engine clip
    useEffect(() => {
        const clip = SequenceCompositionService.getClipById(clipId) as any;
        if (!clip) return;

        const handleStudioSoundChanged = (event: any) => {
            const data = event?.detail ?? event;
            setStudioSoundEnabled(data.enabled);
            // Clear peaks to trigger refetch with new denoised flag
            setPeaks(null);
        };

        // Subscribe to studioSoundChanged event
        const listenerId = clip.on?.('studioSoundChanged', handleStudioSoundChanged);

        // Initialize from current clip state
        if (typeof clip.studioSoundEnabled === 'boolean') {
            setStudioSoundEnabled(clip.studioSoundEnabled);
        }

        return () => {
            if (listenerId) clip.off?.(listenerId);
        };
    }, [clipId]);

    useEffect(() => {
        const disposer = reaction(
            () => ({ filename: SequenceStateStore.getClipTitle(clipId) }),
            ({ filename }) => setFilename(filename),
            { fireImmediately: true }
        );
        return () => disposer();
    }, [clipId]);

    useEffect(() => {
        const trackId = SequenceStateStore.clipIdToAssetId?.[clipId];
        if (!trackId) return;
        (async () => {
            try {
                // When Studio Sound is enabled, check if denoised waveform is available
                if (studioSoundEnabled) {
                    const track = ProjectAssetsStore.projectTracks.find((x: any) => x.id === trackId || x.trackId === trackId);
                    // Only fetch denoised waveform if it's ready
                    if (track?.enhancedTrackState === 'ready') {
                        const data = await GetWaveformData({ trackId, denoised: true });
                        setPeaks(data && data.length ? data : null);
                    } else {
                        const data = await GetWaveformData({ trackId, denoised: false });
                        setPeaks(data && data.length ? data : null);
                    }
                } else {
                    const data = await GetWaveformData({ trackId, denoised: false });
                    setPeaks(data && data.length ? data : null);
                }
            } catch {}
            try { const url = await GetThumbnailUrl({ thumbnailId: trackId, projectId: ProjectAssetsStore.projectId as string }); setThumbnail(url || undefined); } catch {}
        })();
    }, [clipId, studioSoundEnabled]);

    // Re-render when waveform becomes available later, or when denoised state changes
    useEffect(() => {
        const trackId = SequenceStateStore.clipIdToAssetId?.[clipId];
        if (!trackId) return;
        const disposer = reaction(
            () => {
                const t = ProjectAssetsStore.projectTracks.find((x: any) => x.id === trackId || x.trackId === trackId);
                // Watch both regular waveform and denoised state
                return {
                    hasWaveform: t?.hasWaveform === true,
                    enhancedState: t?.enhancedTrackState,
                };
            },
            async ({ hasWaveform, enhancedState }) => {
                // Fetch waveform when it becomes available
                // Use enhanced waveform if Studio Sound is enabled and enhanced is ready
                const useEnhanced = studioSoundEnabled && enhancedState === 'ready';
                if (hasWaveform || (useEnhanced && enhancedState === 'ready')) {
                    try {
                        const data = await GetWaveformData({ trackId, denoised: useEnhanced });
                        setPeaks(data && data.length ? data : null);
                    } catch {}
                }
            },
            { fireImmediately: true }
        );
        return () => { disposer(); };
    }, [clipId, studioSoundEnabled]);

    const displayPeaks = (() => {
        if (!peaks || !peaks.length) return null;
        const clip: any = SequenceCompositionService.getClipById(clipId);
        if (!clip) return peaks;
        const mediaDur = clip.duration?.seconds ?? 0;
        if (!mediaDur) return peaks;
        const segs: Array<{ srcStartSec: number; srcEndSec: number; compStartSec: number }> = clip.segments ?? [];
        if (Array.isArray(segs) && segs.length > 0) {
            const parts: Float32Array[] = [];
            let total = 0;
            for (const seg of segs) {
                const s = Math.max(0, Math.min(mediaDur, seg.srcStartSec ?? 0));
                const e = Math.max(0, Math.min(mediaDur, seg.srcEndSec ?? 0));
                if (e <= s) continue;
                const i0 = Math.max(0, Math.floor((s / mediaDur) * peaks.length));
                const i1 = Math.min(peaks.length, Math.floor((e / mediaDur) * peaks.length));
                if (i1 > i0) { const sub = peaks.subarray(i0, i1); parts.push(sub); total += sub.length; }
            }
            if (!parts.length) return new Float32Array();
            const out = new Float32Array(total);
            let cursor = 0;
            for (const part of parts) { out.set(part, cursor); cursor += part.length; }
            return out;
        }
        const inSec = clip.range?.[0]?.seconds ?? 0;
        const outSec = clip.range?.[1]?.seconds ?? mediaDur;
        const startIdx = Math.max(0, Math.floor((inSec / mediaDur) * peaks.length));
        const endIdx = Math.min(peaks.length, Math.floor((outSec / mediaDur) * peaks.length));
        return peaks.subarray(startIdx, Math.max(startIdx, endIdx));
    })();

    const duration = SequenceStateStore.clips?.[clipId]?.trimmedDuration ?? 0;
    const shouldExpectWaveform = (() => {
        const trackId = SequenceStateStore.clipIdToAssetId?.[clipId];
        if (!trackId) return true;
        const t = ProjectAssetsStore.projectTracks.find((x: any) => x.id === trackId || x.trackId === trackId);
        if (!t) return true;
        if (t?.hasAudioStream === false) return false;
        const stateLower = String(t?.transcriptionState || '').toLowerCase();
        if (stateLower === 'not_needed' || stateLower === 'silent') return false;
        return true;
    })();
    const isProcessing = shouldExpectWaveform && !displayPeaks && duration > 0;

    return (
        <SequenceClip
            clipId={clipId}
            layerId={layerId}
            filename={filename}
            thumbnail={thumbnail}
            backgroundColor={"210, 236, 246"}
            clipVariant="media"
        >
            {/* Waveform goes in the main content area */}
            {displayPeaks && duration > 0 && (
                <div style={{ position: 'absolute', inset: 0, opacity: 0.6 }}>
                    <WaveformDisplay
                        peaks={displayPeaks}
                        duration={duration}
                        rangeEnabled={false}
                        isAudioClip={false}
                        selectedRange={null}
                        waveformColor={SequenceStateStore.getWaveformColor(clipId)}
                    />
                </div>
            )}
            {isProcessing && (
                <div style={{
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 6px',
                    backgroundColor: 'rgba(55, 65, 81, 0.85)',
                    borderRadius: '4px',
                    zIndex: 10,
                }}>
                    <div style={{
                        width: '10px',
                        height: '10px',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        borderTopColor: '#fff',
                        borderRadius: '50%',
                        animation: 'clip-spin 1s linear infinite',
                    }} />
                    <span style={{
                        color: '#fff',
                        fontSize: '10px',
                        fontWeight: 500,
                    }}>Processing...</span>
                </div>
            )}
            <style>{`
                @keyframes clip-spin {
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </SequenceClip>
    );
});

export default SequenceVideoClip;
